import { RefObject, useEffect, useState } from 'react';

interface UseIntersectionObserverOptions extends IntersectionObserverInit {
  freezeOnceVisible?: boolean;
  fallbackInView?: boolean;
}

export function useIntersectionObserver<TElement extends Element>(
  ref: RefObject<TElement>,
  {
    threshold = 0,
    root = null,
    rootMargin = '0px',
    freezeOnceVisible = false,
    fallbackInView = true,
  }: UseIntersectionObserverOptions = {}
): boolean {
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    if (freezeOnceVisible && isIntersecting) {
      return;
    }

    const target = ref.current;
    if (!target) {
      return;
    }

    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setIsIntersecting(fallbackInView);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
      },
      { threshold, root, rootMargin }
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [
    fallbackInView,
    freezeOnceVisible,
    isIntersecting,
    ref,
    root,
    rootMargin,
    threshold,
  ]);

  return isIntersecting;
}
